import { useQueries, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAutomations } from '@/hooks/useAutomations'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { useWorkspace } from '@/hooks/useWorkspace'
import { getDashboards } from '@/services/api/dashboard.service'
import { getRoles } from '@/services/api/permission.service'
import { getRecord } from '@/services/api/record.service'
import { getTag } from '@/services/api/tag.service'
import type {
  CatalogAction,
  CatalogResource,
  PermissionCatalog,
  PermissionScope,
  PermissionSource,
} from '@/types/api/permission'
import type { RecordDetail } from '@/types/api/record'
import type { Tag } from '@/types/api/tag'

export const SCOPE_LABELS: Record<PermissionScope, string> = {
  GLOBAL: 'Everywhere',
  NODE: 'This item',
  SUBTREE: 'Group and its sub-groups',
  GROUP_NODE: 'Everything in the group',
  GROUP_SUBTREE: 'Everything in the group and sub-groups',
}

export const SCOPE_BADGE_CLASSES: Record<PermissionScope, string> = {
  GLOBAL: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  NODE: 'bg-blue-50 text-blue-700 border-blue-200',
  SUBTREE: 'bg-purple-50 text-purple-700 border-purple-200',
  GROUP_NODE: 'bg-amber-50 text-amber-700 border-amber-200',
  GROUP_SUBTREE: 'bg-orange-50 text-orange-700 border-orange-200',
}

/** A grant on specific resources (not workspace-wide), shown in the "specific items" table. */
export interface ScopedGrantRow {
  rowKey: string
  permissionKey: string
  resourceType: string
  resourceId: string | null
  scope: PermissionScope
  /** Where the grant comes from (user page); empty for a role's own grants. */
  sources: PermissionSource[]
  /** Grant id the current page may revoke, or null if it is inherited. */
  revokeGrantId: string | null
}

export interface CatalogIndex {
  catalog: PermissionCatalog
  actionByKey: Map<string, CatalogAction>
  resourceByKey: Map<string, CatalogResource>
  resourceByType: Map<string, CatalogResource>
  categories: PermissionCatalog['categories']
  /** For each key, the keys that include it (transitively). */
  includedBy: Map<string, string[]>
}

export function indexCatalog(catalog: PermissionCatalog): CatalogIndex {
  const actionByKey = new Map<string, CatalogAction>()
  const resourceByKey = new Map<string, CatalogResource>()
  const resourceByType = new Map<string, CatalogResource>()
  for (const resource of catalog.resources) {
    resourceByType.set(resource.resourceType, resource)
    for (const action of resource.actions) {
      actionByKey.set(action.key, action)
      resourceByKey.set(action.key, resource)
    }
  }
  const categories = [...catalog.categories]
    .filter((c) => catalog.resources.some((r) => r.category === c.id))
    .sort((a, b) => a.order - b.order)
  const includedBy = new Map<string, string[]>()
  for (const action of actionByKey.values()) {
    for (const included of action.includes) {
      includedBy.set(included, [...(includedBy.get(included) ?? []), action.key])
    }
  }
  return { catalog, actionByKey, resourceByKey, resourceByType, categories, includedBy }
}

/**
 * Workspace-wide keys included in {@code held} (e.g. holding "Edit records" includes "View records"),
 * mapped to the held keys that include them. Mirrors the backend's implication rules.
 */
export function includedKeys(index: CatalogIndex, held: Iterable<string>): Map<string, string[]> {
  const result = new Map<string, string[]>()
  for (const key of held) {
    for (const included of index.actionByKey.get(key)?.includes ?? []) {
      result.set(included, [...(result.get(included) ?? []), key])
    }
  }
  return result
}

/** Actions a grant can target at specific resources (used by the "add specific access" dialog). */
export function instanceGrantableActions(index: CatalogIndex, category?: string) {
  return index.catalog.resources
    .filter((r) => !category || r.category === category)
    // widget visibility is managed from the dashboard itself
    .filter((r) => r.resourceType !== 'DASHBOARD_WIDGET')
    .flatMap((resource) =>
      resource.actions
        .filter((a) => a.scopes.some((s) => s !== 'GLOBAL'))
        .map((action) => ({ action, resource }))
    )
}

export function sourceLabel(source: PermissionSource, labelOf: (key: string) => string = (k) => k): string {
  switch (source.type) {
    case 'OWNER':
      return 'Workspace owner'
    case 'ADMIN':
      return `Admin role${source.roleName ? ` (${source.roleName})` : ''}`
    case 'ROLE':
      return source.roleName ? `Role: ${source.roleName}` : 'Role'
    case 'OVERRIDE':
      return 'User override'
    case 'IMPLIED':
      return `Included in ${source.impliedBy ? labelOf(source.impliedBy) : 'another permission'}${source.roleName ? ` (Role: ${source.roleName})` : ''}`
  }
}

type ResolvableRow = Pick<ScopedGrantRow, 'resourceType' | 'resourceId' | 'scope'>

const isRecordRow = (row: ResolvableRow) =>
  !!row.resourceId && (row.resourceType === 'RECORD' || row.resourceType === 'RECORD_ACTIVITY')
  && row.scope !== 'SUBTREE' && row.scope !== 'GROUP_NODE' && row.scope !== 'GROUP_SUBTREE'

const isTagRow = (row: ResolvableRow) => !!row.resourceId && row.resourceType === 'TAG' && row.scope === 'NODE'

const NO_ROWS: readonly ResolvableRow[] = []

const titlesById = (results: { data?: RecordDetail }[]) =>
  new Map(results.flatMap((r) => (r.data ? [[r.data.recordId, r.data.title || 'Untitled record'] as const] : [])))

/**
 * Resolves resource ids to names for the grants table (falls back to a short id).
 * Titles of record-scoped grants among {@code rows} are fetched individually.
 */
export function useResourceNameResolver(rows: readonly ResolvableRow[] = NO_ROWS) {
  const { currentWorkspaceId } = useWorkspace()
  const { recordGroups = [] } = useRecordGroups()
  const { automations = [] } = useAutomations()
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: getRoles })
  const { data: dashboards = [] } = useQuery({ queryKey: ['dashboards', 'names'], queryFn: getDashboards })

  const recordIds = useMemo(
    () => [...new Set(rows.filter(isRecordRow).map((row) => row.resourceId!))],
    [rows]
  )
  const recordTitles = useQueries({
    queries: recordIds.map((id) => ({
      // Same key as useRecordDetail, so an opened record's cache is reused.
      queryKey: ['workspace', currentWorkspaceId, 'records', id],
      queryFn: () => getRecord(id, { suppressErrorToast: true }),
      enabled: !!currentWorkspaceId,
      retry: false,
    })),
    combine: titlesById,
  })

  const tagIds = useMemo(
    () => [...new Set(rows.filter(isTagRow).map((row) => row.resourceId!))],
    [rows]
  )
  const tagLabels = useQueries({
    queries: tagIds.map((id) => ({
      queryKey: ['tag', id],
      queryFn: () => getTag(id, { suppressErrorToast: true }),
      enabled: !!currentWorkspaceId,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results: { data?: Tag }[]) =>
      new Map(results.flatMap((r) => (r.data ? [[r.data.id, r.data.label] as const] : []))),
  })

  return useMemo(() => {
    const groups = new Map(recordGroups.map((g) => [g.id, g.name]))
    const automationNames = new Map(automations.map((a) => [a.id, `Automation · ${a.triggerEvent}`]))
    const roleNames = new Map(roles.map((r) => [r.id, r.name]))
    const dashboardNames = new Map(dashboards.map((d) => [d.id, d.title]))
    const short = (id: string) => id.substring(0, 8)

    return (row: ResolvableRow): string => {
      const id = row.resourceId
      if (!id) return 'Everywhere'
      if (row.scope === 'SUBTREE' || row.scope === 'GROUP_NODE' || row.scope === 'GROUP_SUBTREE'
        || row.resourceType === 'RECORD_GROUP') {
        return `Record group · ${groups.get(id) ?? short(id)}`
      }
      switch (row.resourceType) {
        case 'AUTOMATION':
          return automationNames.get(id) ?? `Automation · ${short(id)}`
        case 'ROLE':
          return `Role · ${roleNames.get(id) ?? short(id)}`
        case 'DASHBOARD':
          return `Dashboard · ${dashboardNames.get(id) ?? short(id)}`
        case 'FORM':
          return `Form · ${short(id)}`
        case 'RECORD':
        case 'RECORD_ACTIVITY':
          return `Record · ${recordTitles.get(id) ?? short(id)}`
        case 'TAG':
          return `Tag · ${tagLabels.get(id) ?? short(id)}`
        default:
          return short(id)
      }
    }
  }, [recordGroups, automations, roles, dashboards, recordTitles, tagLabels])
}
